import {
  APIConstraintEnforcementMode,
  ArdoqId,
  LinkedComponent,
} from '@ardoq/api-types';
import { DependencyMatrixCell, Row, RowHeader } from '../atoms';
import {
  ComponentItemData,
  ComponentItemDataById,
  DependencyMatrixViewSettings,
} from '../types';
import { ComponentLabel } from './ComponentLabel';
import { ReferenceCell } from './ReferenceCell';
import { useCallback } from 'react';
import { dependencyMatrixCommands } from '../commands';
import { IntendedRefTypes } from '../../../utils/metamodelConstraints';

type DependencyMatrixRowProps = {
  componentId: ArdoqId;
  component: ComponentItemData;
  targetComponentItemDataById: ComponentItemDataById;
  referenceCssClassNamesById: Record<ArdoqId, string>;
  selectedReferenceTypeName: string | null;
  workspaceId: ArdoqId;
  selectedComponentId: ArdoqId;
  targetIds: ArdoqId[];
  readOnly: boolean;
  rowReferences: LinkedComponent[];
  viewSettings: DependencyMatrixViewSettings;
  highlightLastClickedTargetId?: ArdoqId;
  focusedComponentId: ArdoqId | null;
  isExporting: boolean;
  constraintEnforcementMode: APIConstraintEnforcementMode;
  intendedRefTypes: IntendedRefTypes | null;
};

const TYPE = 'SOURCE';
export const DependencyMatrixRow = ({
  componentId,
  component,
  targetComponentItemDataById,
  referenceCssClassNamesById,
  selectedReferenceTypeName,
  targetIds,
  readOnly,
  rowReferences,
  viewSettings,
  highlightLastClickedTargetId,
  isExporting,
  workspaceId,
  selectedComponentId,
  focusedComponentId,
  constraintEnforcementMode,
  intendedRefTypes,
}: DependencyMatrixRowProps) => {
  const isComponentFocused = focusedComponentId === componentId;
  const isComponentSelected = selectedComponentId === componentId;

  const targetReferencesMap: Record<ArdoqId, LinkedComponent[]> =
    Object.fromEntries(
      targetIds.map(targetId => [
        targetId,
        rowReferences.filter(
          ({ componentId: referencedComponentId }) =>
            targetId === referencedComponentId
        ),
      ])
    );

  const onComponentClick = useCallback(
    (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
      e.stopPropagation();
      dependencyMatrixCommands.setFocusedItemState(component.id);
    },
    [component.id]
  );

  return (
    <Row
      key={component.id}
      data-component-id={component.id}
      $isLastClicked={isComponentFocused}
      $isSelected={isComponentSelected}
      onClick={onComponentClick}
    >
      <RowHeader $isSelected={isComponentSelected}>
        <ComponentLabel type={TYPE} component={component} key={component.id} />
      </RowHeader>

      {!isExporting ? <DependencyMatrixCell /> : null}
      <DependencyMatrixCell />

      {targetIds.map(targetId => (
        <ReferenceCell
          key={targetId}
          readOnly={readOnly}
          sourceId={componentId}
          targetId={targetId}
          sourceComponent={component}
          targetComponent={targetComponentItemDataById[targetId]}
          references={targetReferencesMap[targetId]}
          referenceCssClassNamesById={referenceCssClassNamesById}
          selectedReferenceTypeName={selectedReferenceTypeName}
          viewSettings={viewSettings}
          workspaceId={workspaceId}
          highlightAsLastClicked={
            highlightLastClickedTargetId === targetId && !isExporting
          }
          constraintEnforcementMode={constraintEnforcementMode}
          intendedRefTypes={intendedRefTypes}
        />
      ))}
    </Row>
  );
};
